import { type BrokerConnection, type BrokerSelection, brokerAvailability } from "@rally/agreed";
import { howLongAgo } from "../localization/format.ts";
import { say } from "../localization/say.ts";
import { read } from "../net/rally.ts";
import { providerCalled } from "./a-provider.ts";
import type { HeldCredential } from "./held.ts";

export type AgentConnection = {
  id: string;
  label: string;
  provider: string;
  model?: string | null;
  broker?: BrokerSelection;
  pool?: BrokerConnection;
};

export function connectionId(agent: { credential: string | null; broker?: BrokerSelection | null }) {
  return agent.broker
    ? `broker:${agent.broker.connection}:${agent.broker.account ?? "*"}`
    : (agent.credential ?? "");
}

export async function agentConnections(): Promise<AgentConnection[]> {
  const [locals, pools] = await Promise.all([
    read<HeldCredential[]>("shared.providers"),
    read<BrokerConnection[]>("my.broker-connections").catch(() => []),
  ]);
  return [
    ...locals.map((one) => ({
      id: one.id,
      label: `${say("agents.localConnection")} · ${one.label}`,
      provider: one.provider,
      model: one.model,
    })),
    ...pools.flatMap((pool) =>
      [null, ...pool.accounts.map((one) => one.id)].map((account) => {
        const broker = { connection: pool.id, account };
        return {
          id: connectionId({ credential: null, broker }),
          label: `${say("agents.brokerAccount")} · ${account ?? say("agents.automatic")} · ${providerCalled(pool.provider)}`,
          provider: pool.provider,
          broker,
          pool,
        };
      }),
    ),
  ];
}

export function connectionPayload(connection: AgentConnection | undefined, id: string) {
  return connection?.broker ? { broker: connection.broker } : { credential: id };
}

export function connectionStatus(connection: AgentConnection | undefined, model: string): string | null {
  const pool = connection?.pool;
  if (!pool) return null;
  const lines = pool.accounts
    .filter((one) => connection.broker?.account === null || one.id === connection.broker?.account)
    .map((one) => {
      const status = brokerAvailability(pool, one, model);
      const labels = {
        available: say("agents.brokerAvailable"),
        unavailable: say("agents.brokerUnavailable"),
        exhausted: say("agents.brokerExhausted"),
        cooldown: say("agents.brokerCooldown"),
      };
      const windows: Record<string, string> = {
        primary: say("agents.brokerPrimary"),
        secondary: say("agents.brokerSecondary"),
        "5h": say("agents.broker5h"),
        "7d": say("agents.broker7d"),
        "7d OI": say("agents.broker7dOi"),
      };
      const meter = one.windows.length
        ? one.windows
            .map(
              (window) =>
                `${windows[window.name] ?? window.name}: ${window.used}%${window.resetAt ? ` · ${say("agents.brokerReset")} ${new Date(window.resetAt).toLocaleString()}` : ""}`,
            )
            .join(" · ")
        : say("agents.brokerMeterUnknown");
      return `${one.id} — ${labels[status]}\n${meter}\n${one.meteredAt ? say("agents.brokerMeterAge")(howLongAgo(new Date(one.meteredAt).toISOString())) : say("agents.brokerAgeUnknown")}`;
    });
  return [
    `${say("agents.brokerAccount")} · ${pool.instance}`,
    say("agents.brokerNextTurnOnly"),
    ...lines,
    `${say("agents.brokerCached")} ${pool.syncedAt ? howLongAgo(pool.syncedAt) : say("agents.brokerMeterUnknown")}`,
  ].join("\n");
}
